import type { Issue } from './types';
import { can, type Role } from './permissions';

/**
 * Which system owns which field.
 *
 * A project either owns its own issues, or mirrors an external tracker. Where it
 * mirrors one, these are the fields that tracker owns: every push overwrites
 * them wholesale. Editing one on the board is not "not saved" — it is saved, and
 * then silently undone the next time a push runs, which is worse.
 *
 * Everything NOT listed here belongs to the board and survives every push:
 * comments, assignee, and the blocker classification.
 *
 * This list must match TRACKER_FIELDS in scripts/push-tracker.mjs. A test holds
 * the two together, because if they drift the board starts lying about what it
 * is safe to edit.
 */
export const TRACKER_OWNED: (keyof Issue)[] = [
  'ref',
  'kind',
  'title',
  'summary',
  'blocked',
  'stands',
  'epic',
  'description',
  'status',
  'priority',
  'labels',
  'builds',
  'commits',
];

export type IssueSource = 'board' | 'tracker';

/** Both kinds, in the order they are offered. */
export const SOURCES: IssueSource[] = ['board', 'tracker'];

export const SOURCE_LABEL: Record<IssueSource, string> = {
  board: 'This board',
  tracker: 'An external tracker',
};

export const SOURCE_BLURB: Record<IssueSource, string> = {
  board:
    'Issues are written and kept up to date here. Everyone edits them on the board and nothing overwrites anyone. This is how most teams work.',
  tracker:
    'Issues are kept somewhere else — a file, a script, or another system — and copied onto this board. The board shows them and adds comments and assignees on top, and the fields the other system owns are read-only here.',
};

/** What to call the external source when a project has not named its own. */
export const DEFAULT_SOURCE_NAME = 'the external tracker';

/** Can this field be edited here, or does something else own it? */
export function ownedByBoard(field: keyof Issue, source: IssueSource): boolean {
  return source === 'board' || !TRACKER_OWNED.includes(field);
}

/**
 * May cards be dragged between columns?
 *
 * Dragging writes status, which an external tracker owns — so allowing it would
 * move the card and let the next push move it back. Two components asked this
 * question separately and gave different answers, so it lives here now.
 */
export function canMoveCards(role: Role, source: IssueSource): boolean {
  return can(role, 'moveIssue') && source === 'board';
}

/**
 * May issues be raised here?
 *
 * Not on a board fed from a tracker: the title is one of the fields that tracker
 * owns, so the form opens with the title box greyed out and the save refuses
 * without one. The button was offered anyway — press it, fill in what you can,
 * press Create issue, and nothing happened and nothing said why.
 *
 * Offering an action and then refusing it is a fault even though nothing unsafe
 * occurred, so the button is not offered. Where it was, the sidebar says where
 * issues come from instead, so its absence is explained rather than silent.
 */
export function canCreateIssues(role: Role, source: IssueSource): boolean {
  return can(role, 'createIssue') && source === 'board';
}
